import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchRecipients, getApiError } from "./emailApi";

const sortByEmail = (list) =>
  [...list].sort((a, b) =>
    a.email.localeCompare(b.email, undefined, { sensitivity: "base" }),
  );

const useEmailRecipients = (currentSession) => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRecipients(sortByEmail(await fetchRecipients(currentSession)));
    } catch (e) {
      setError(getApiError(e, "Failed to load saved recipients."));
    } finally {
      setLoading(false);
    }
  }, [currentSession]);

  useEffect(() => {
    reload();
  }, [reload]);

  const upsertMany = useCallback((items) => {
    setRecipients((prev) => {
      const ids = new Set(items.map((i) => i.id));
      return sortByEmail([...prev.filter((r) => !ids.has(r.id)), ...items]);
    });
  }, []);

  const upsert = useCallback((item) => upsertMany([item]), [upsertMany]);

  const remove = useCallback(
    (id) => setRecipients((prev) => prev.filter((r) => r.id !== id)),
    [],
  );

  // lowercase email → recipient
  const byEmail = useMemo(
    () => new Map(recipients.map((r) => [r.email.toLowerCase(), r])),
    [recipients],
  );

  return {
    recipients,
    byEmail,
    loading,
    error,
    reload,
    upsert,
    upsertMany,
    remove,
  };
};

export default useEmailRecipients;
